import React, { useEffect, useState } from "react";
import { SlidersHorizontal, X } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { useApp } from "@/context/AppContext";
import { GENRES, TYPES, RADII, nextMonths, monthRange } from "@/lib/constants";

const Field = ({ label, children }) => (
  <div><label className="eyebrow block mb-1.5">{label}</label>{children}</div>
);

export default function FilterPanel({ value, onApply, activeCount }) {
  const { t, lang } = useApp();
  const [open, setOpen] = useState(false);
  const [d, setD] = useState(value);
  useEffect(() => { if (open) setD(value); }, [open, value]);
  const set = (k, v) => setD(x => ({ ...x, [k]: v }));
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
  const monthLabel = ym => { const [y, m] = ym.split("-"); return `${t("monthHeaders")[+m - 1]} ${y}`; };

  const apply = () => { onApply(d); setOpen(false); };
  const clear = () => { onApply({}); setOpen(false); };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button className="gg-btn !py-2 !px-3 text-xs relative" data-testid="open-filters">
          <SlidersHorizontal className="w-4 h-4" /> {t("advancedFilters")}
          {activeCount > 0 && <span className="ml-1 bg-primary text-primary-foreground px-1.5 leading-4 text-[10px]" data-testid="filters-count">{activeCount}</span>}
        </button>
      </SheetTrigger>
      <SheetContent side={isMobile ? "bottom" : "right"} className="!rounded-none border-foreground bg-background p-0 max-h-[88vh] overflow-y-auto md:max-w-md" data-testid="filter-panel">
        <SheetHeader className="p-4 border-b border-foreground text-left">
          <SheetTitle className="font-display uppercase text-2xl tracking-tighter">{t("filters")}</SheetTitle>
        </SheetHeader>
        <div className="p-4 grid grid-cols-2 gap-4">
          <Field label={t("dateFrom")}><input type="date" className="gg-input" value={d.date_from || ""} onChange={e => set("date_from", e.target.value)} data-testid="fp-date-from" /></Field>
          <Field label={t("dateTo")}><input type="date" className="gg-input" value={d.date_to || ""} onChange={e => set("date_to", e.target.value)} data-testid="fp-date-to" /></Field>
          <div className="col-span-2"><Field label={t("month")}>
            <select className="gg-input" value={d.month || ""} data-testid="fp-month"
              onChange={e => { const ym = e.target.value; setD(x => ({ ...x, month: ym, ...(ym ? monthRange(ym) : { date_from: "", date_to: "" }) })); }}>
              <option value="">{t("anyMonth")}</option>
              {nextMonths().map(ym => <option key={ym} value={ym}>{monthLabel(ym)}</option>)}
            </select></Field></div>
          <div className="col-span-2"><Field label={t("genre")}>
            <div className="flex flex-wrap gap-1.5" data-testid="fp-genres">
              {GENRES.map(g => { const gl = (d.genre || "").split(",").filter(Boolean); const on = gl.includes(g); return (
                <button key={g} type="button" data-testid={`fp-genre-${g}`} onClick={() => set("genre", (on ? gl.filter(x => x !== g) : [...gl, g]).join(","))} className={`gg-chip !py-2 min-h-[36px] ${on ? "gg-chip-active" : "hover:bg-muted"}`}>{t(`genres.${g}`)}</button>); })}
            </div></Field></div>
          <Field label={t("eventType")}>
            <select className="gg-input" value={d.event_type || ""} onChange={e => set("event_type", e.target.value)} data-testid="fp-type">
              <option value="">{t("allTypes")}</option>{TYPES.map(x => <option key={x} value={x}>{t(`types.${x}`)}</option>)}
            </select></Field>
          <Field label={t("country")}><input className="gg-input" placeholder={t("anyCountry")} value={d.country || ""} onChange={e => set("country", e.target.value)} data-testid="fp-country" /></Field>
          <Field label={t("city")}><input className="gg-input" placeholder={t("anyCity")} value={d.nearMe ? "" : (d.city || "")} onChange={e => set("city", e.target.value)} data-testid="fp-city" /></Field>
          <div className="col-span-2"><Field label={t("distance")}>
            <div className="grid grid-cols-4 border border-foreground">
              {["", ...RADII].map((r, i) => (
                <button key={r} type="button" data-testid={`fp-radius-${r || "any"}`} onClick={() => set("radius_km", r)}
                  className={`py-3 font-display uppercase text-xs tracking-wider ${i > 0 ? "border-l border-foreground" : ""} ${String(d.radius_km || "") === String(r) ? "bg-foreground text-background" : ""}`}>
                  {r ? `${r} km` : t("anyDistance")}
                </button>
              ))}
            </div></Field></div>
        </div>
        <div className="p-4 border-t border-foreground grid grid-cols-2 gap-3 sticky bottom-0 bg-background">
          <button onClick={clear} className="gg-btn" data-testid="fp-clear"><X className="w-4 h-4" />{t("clearAll")}</button>
          <button onClick={apply} className="gg-btn-primary" data-testid="fp-apply">{t("applyFilters")}</button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
